import React, { Fragment, useEffect, useState, useMemo } from "react";
import {
  Box,
  Container,
  Typography,
  Card,
  CardContent,
  Button,
  Grid,
  Paper,
  CircularProgress,
  Backdrop,
  Alert
} from "@mui/material";
import { ArrowBack as ArrowBackIcon, Download as DownloadIcon } from "@mui/icons-material";
import axios from "axios";
import { useParams, Link, useNavigate } from "react-router-dom";
import BarChart from "../../components/BarChart/BarChart";
import { BASE_URL, domain } from "../../variables/api";

const Summary = (props) => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [formData, setFormData] = useState({
    name: "",
    FormArr: [],
    description: "",
  });

  useEffect(() => {
    setLoading(true);
    setError(null);
    
    axios.post(`${BASE_URL}/surveys/getsummary.php`, { id })
      .then((res) => {
        if (res.data && res.data.data) {
          setFormData((prevstate) => ({
            ...prevstate,
            name: res.data.data.summary?.[0]?.name || "Survey",
            FormArr: res.data.data.responses || [],
          }));
        }
      })
      .catch((err) => {
        console.error("Error fetching summary:", err);
        setError("Failed to load survey summary. Please try again.");
      })
      .finally(() => {
        setLoading(false);
      });
  }, [id]);

  const { data, labels, questions } = useMemo(() => {
    let dataObj = {};
    let labelsObj = {};
    let questionsArr = [];

    if (formData.FormArr && Array.isArray(formData.FormArr) && formData.FormArr.length > 0) {
      // Use the first response to get the question structure
      questionsArr = Array.isArray(formData.FormArr[0]) ? formData.FormArr[0] : [];

      formData.FormArr.forEach((responseArr) => {
        if (!Array.isArray(responseArr)) return;
        
        responseArr.forEach((response) => {
          if (!response || !response.id || !response.type) return;
          
          // Process Labels (X-Axis)
          if (!labelsObj[response.id]) {
             const xaxis = response.options?.map((option) => {
                const typeValue = response.type?.value;
                if (
                  typeValue === "radio" ||
                  typeValue === "linescale" ||
                  typeValue === "select"
                ) {
                  return option?.value || '';
                } else if (typeValue === "checkbox") {
                  // Truncate or format label logic
                  let val = option?.value || '';
                  if (val?.split(":")[0]?.length <= 20) {
                    return val.split(":")[0];
                  } else {
                     return val.substring(0, 20) + "...";
                  }
                }
                return option?.value || '';
             }) || [];
             labelsObj[response.id] = xaxis;
          }

          // Process Data (Counts)
          const yaxis = response.options?.map((option) => option?.id) || [];
          if (!dataObj[response.id]) {
            dataObj[response.id] = yaxis.reduce((a, v) => ({ ...a, [v]: 0 }), {});
          }

          response.options?.forEach((option) => {
            if (option?.response === true) {
              if (dataObj[response.id] && option?.id && dataObj[response.id][option.id] !== undefined) {
                 dataObj[response.id][option.id]++;
              }
            }
          });
        });
      });
    }

    return { data: dataObj, labels: labelsObj, questions: questionsArr };
  }, [formData.FormArr]);

  const backgroundColors = [
    "#FF6633", "#FFB399", "#FF33FF", "#FFFF99", "#00B3E6",
    "#E6B333", "#3366E6", "#999966", "#99FF99", "#B34D4D",
    "#80B300", "#809900", "#E6B3B3", "#6680B3", "#66991A",
    "#FF99E6", "#CCFF1A", "#FF1A66", "#E6331A", "#33FFCC",
    "#66994D", "#B366CC", "#4D8000", "#B33300", "#CC80CC",
    "#66664D", "#991AFF", "#E666FF", "#4DB3FF", "#1AB399",
    "#E666B3", "#33991A", "#CC9999", "#B3B31A", "#00E680",
    "#4D8066", "#809980", "#E6FF80", "#1AFF33", "#999933",
    "#FF3380", "#CCCC00", "#66E64D", "#4D80CC", "#9900B3",
    "#E64D66", "#4DB380",
  ];

  return (
    <Box sx={{ p: 3 }}>
      {/* Loading Overlay */}
      <Backdrop
        sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }}
        open={loading}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
          <CircularProgress color="inherit" />
          <Typography variant="h6">Loading Summary...</Typography>
        </Box>
      </Backdrop>

      <Button
        component={Link}
        to="/"
        startIcon={<ArrowBackIcon />}
        sx={{ mb: 3 }}
        variant="outlined"
      >
        Back to Dashboard
      </Button>

      {/* Error Alert */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {!loading && !error && (
        <>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4, flexWrap: 'wrap', gap: 2 }}>
            <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
              {formData.name || "Survey"} Summary
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                <Button
                    variant="contained"
                    color="primary"
                    startIcon={<DownloadIcon />}
                    href={window.location.href.search("iatseg.org") != -1 
                        ? `https://survey.iatseg.org/backend/surveys/export.php?id=${id}`
                        : `https://survey.${domain}/backend/surveys/export.php?id=${id}`
                    }
                >
                    Export CSV
                </Button>
                <Paper elevation={0} sx={{ p: 1, px: 2, bgcolor: 'primary.main', color: 'white', borderRadius: 1 }}>
                    <Typography variant="subtitle1" fontWeight="bold">
                        Total Responses: {formData.FormArr?.length || 0}
                    </Typography>
                </Paper>
            </Box>
          </Box>

          {(!formData.FormArr || formData.FormArr.length === 0) ? (
             <Paper sx={{ p: 4, textAlign: 'center' }}>
                <Typography variant="h6" color="textSecondary">No responses found for this survey.</Typography>
                <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
                  Once respondents submit responses, you'll see the summary data here.
                </Typography>
             </Paper>
          ) : (
        <Grid container spacing={3}>
          {questions?.map((details, i) => {
            // Add null checks for details and its properties
            if (!details || !details.id || !details.type || !data[details.id]) return null;

            // Determine chart data based on type
            let chartData = {};
            const typeValue = details.type?.value;
            
            if (typeValue === "checkbox") {
               chartData = {
                 labels: labels[details.id] || [],
                 datasets: details.options?.map((option, index) => {
                    let datatemp = new Array(details.options?.length || 0).fill(0);
                    const optionKey = `option-${index + 1}`;
                    datatemp[index] = data[details.id]?.[optionKey] || 0;
                    return {
                      label: option?.value || `Option ${index + 1}`,
                      data: datatemp,
                      backgroundColor: backgroundColors[index % backgroundColors.length],
                    };
                 }) || []
               };
            } else {
               // Radio, Select, Linescale
               chartData = {
                 labels: labels[details.id] || [],
                 datasets: [
                   {
                     label: details.Question || 'Question',
                     data: Object.values(data[details.id] || {}),
                     backgroundColor: ["#3278bd"],
                   },
                 ],
               };
            }

            if (["checkbox", "radio", "select", "linescale"].includes(typeValue)) {
              return (
                <Grid item xs={12} md={6} key={details.id || i}>
                  <Card sx={{ height: '100%', borderRadius: 2, boxShadow: 2 }}>
                    <CardContent>
                      <Typography variant="h6" gutterBottom align="center">
                        {details.Question || 'Question'}
                      </Typography>
                      <Box sx={{ height: 300, display: 'flex', justifyContent: 'center' }}>
                        <BarChart
                          labels={chartData.labels}
                          datasets={chartData.datasets}
                          text={details.Question || 'Question'}
                          stepSize={1}
                          min={0}
                          display={true}
                        />
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
              );
            }
            return null;
          })}
        </Grid>
          )}
        </>
      )}
    </Box>
  );
};

export default Summary;
